import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Calculator, CalendarCheck, PiggyBank, ShieldCheck, ArrowRight } from "@phosphor-icons/react";

const money = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function TaxDashboardCard() {
  const [overview, setOverview] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    api.get("/tax/overview").then((r) => {
      if (!cancelled) setOverview(r.data);
    }).catch(() => {
      if (!cancelled) setOverview({ has_activity: false, business_count: 0, rowan_primary: "Couldn't load tax estimate yet." });
    });
    return () => { cancelled = true; };
  }, []);

  if (!overview) return null;

  const openTaxes = () => {
    if (overview?.nearest_business_id) navigate(`/business/${overview.nearest_business_id}?tab=taxes`);
  };

  return (
    <section className="rounded-[28px] p-6 sm:p-7 mb-10 overflow-hidden relative" style={{ background: "linear-gradient(135deg, rgba(255,249,240,0.98) 0%, rgba(246,238,223,0.96) 100%)", border: "1px solid rgba(232,168,56,0.28)" }} data-testid="dashboard-tax-card">
      <div className="absolute -right-12 -top-12 w-40 h-40 rounded-full" style={{ background: "rgba(232,168,56,0.12)" }} />
      <div className="relative flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold" style={{ background: "rgba(255,255,255,0.7)", color: "var(--bk-accent)" }}>
            <Calculator size={14} weight="duotone" /> Tax Estimate
          </div>
          <h3 className="font-display text-2xl sm:text-3xl font-bold mt-3" style={{ color: "var(--bk-text)" }}>Quarterly tax picture</h3>
          <p className="text-sm mt-2 leading-relaxed" style={{ color: "var(--bk-text-2)" }} data-testid="dashboard-tax-rowan">
            {overview.rowan_primary || "Rowan will turn your books into a tax estimate as soon as there’s enough activity to read."}
          </p>
        </div>

        {overview?.nearest_business_id && (
          <button onClick={openTaxes} data-testid="dashboard-tax-open" className="bk-btn-primary px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2">
            Open taxes <ArrowRight size={15} weight="bold" />
          </button>
        )}
      </div>

      <div className="relative grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mt-6">
        <div className="bk-card p-5" data-testid="dashboard-tax-quarterly">
          <p className="text-xs uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Quarterly estimate</p>
          <p className="font-display text-3xl font-bold mt-2" style={{ color: "var(--bk-text)" }}>{money(overview.quarterly_payment_total || 0)}</p>
          <p className="text-xs mt-2" style={{ color: "var(--bk-text-2)" }}>Across {overview.business_count || 0} business{overview.business_count === 1 ? "" : "es"}</p>
        </div>

        <div className="bk-card p-5" data-testid="dashboard-tax-savings">
          <div className="flex items-center gap-2">
            <PiggyBank size={16} weight="duotone" color="var(--bk-success)" />
            <p className="text-xs uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Actually set aside</p>
          </div>
          <p className="font-display text-3xl font-bold mt-2" style={{ color: "var(--bk-text)" }}>{overview.savings_pct || 0}%</p>
          <p className="text-xs mt-2" style={{ color: "var(--bk-text-2)" }} data-testid="dashboard-tax-savings-note">{money(overview.tax_savings_total || 0)} logged of {money(overview.annual_tax_total || 0)} estimated annual tax · Rowan recommends {money(overview.monthly_set_aside_total || 0)}/mo</p>
        </div>

        <div className="bk-card p-5" data-testid="dashboard-tax-deadline">
          <div className="flex items-center gap-2">
            <CalendarCheck size={16} weight="duotone" color="var(--bk-accent)" />
            <p className="text-xs uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Deadline countdown</p>
          </div>
          <p className="font-display text-3xl font-bold mt-2" style={{ color: "var(--bk-text)" }}>{overview.deadline_days ?? "—"}</p>
          <p className="text-xs mt-2" style={{ color: "var(--bk-text-2)" }}>{overview.next_due_quarter ? `${overview.next_due_quarter} due ${overview.next_due_date}` : "No due date yet"}</p>
        </div>

        <div className="bk-card p-5" data-testid="dashboard-tax-safe-harbor">
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} weight="duotone" color="var(--bk-success)" />
            <p className="text-xs uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--bk-text-2)" }}>90% current-year target</p>
          </div>
          <p className="font-display text-3xl font-bold mt-2" style={{ color: overview.safe_harbor_status === "covered" ? "var(--bk-success)" : "var(--bk-text)" }}>{overview.safe_harbor_pct || 0}%</p>
          <p className="text-xs mt-2" style={{ color: "var(--bk-text-2)" }}>
            {overview.safe_harbor_gap > 0 ? `${money(overview.safe_harbor_gap)} short of the 90% current-year target` : "90% current-year target set aside"}
          </p>
        </div>
      </div>
    </section>
  );
}